// TimetablePeriodConflicts.ts
// Detects real double-bookings: the same teacher scheduled in the same
// period, same day, across two different class/sections. This is distinct
// from a teacher simply teaching several periods across the day (normal),
// which is what the old whole-day `teacherOccurrences` count was flagging.
import { TimetableItem } from './subparts/types';

export interface ConflictClassSection {
  class_id: string;
  section: string | null;
}

export const conflictCellKey = (period: string, classId: string, section: string | null): string =>
  `${period}::${classId}::${section ?? ''}`;

/**
 * Returns the set of cell keys (period::class_id::section) involved in a
 * same-period teacher clash, for O(1) lookup while rendering the grid.
 */
export function detectPeriodTeacherConflicts(
  classSections: ConflictClassSection[],
  uniquePeriods: string[],
  getItems: (classId: string, section: string | null, period: string) => TimetableItem[],
): Set<string> {
  const conflictCells = new Set<string>();

  uniquePeriods.forEach((period) => {
    const teacherCells = new Map<string, string[]>();

    classSections.forEach((cs) => {
      const items = getItems(cs.class_id, cs.section, period);
      items.forEach((item) => {
        if (!item.teacher_name || item.slot_type === 'break') return;
        const key = conflictCellKey(period, cs.class_id, cs.section);
        const list = teacherCells.get(item.teacher_name) ?? [];
        if (!list.includes(key)) list.push(key);
        teacherCells.set(item.teacher_name, list);
      });
    });

    teacherCells.forEach((cellKeys) => {
      if (cellKeys.length > 1) cellKeys.forEach((k) => conflictCells.add(k));
    });
  });

  return conflictCells;
}

// Light red — visible but not heavy.
export const CONFLICT_BG = 'rgba(239, 68, 68, 0.14)';
export const CONFLICT_BG_HOVER = 'rgba(239, 68, 68, 0.22)';